"use client";

import { useCarouselButtons } from "@repo/lib/hooks/common";
import { useDotButton } from "@repo/lib/hooks/common";
import { BadgeDiscount, DotButton, EmbedVideo, ThumbsCarouselButton, ToggleWishlistButton, VideoThumbnailWrapper } from "@repo/components";
import { hasDiscount } from "@repo/lib/utils";
import { Media } from "@repo/ui";
import cx from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
const Thumb = props => {
  const {
    selected,
    thumbNail,
    onClick
  } = props;
  return <div className="min-w-0 flex-none cursor-pointer" onClick={onClick} data-sentry-component="Thumb" data-sentry-source-file="MediaGallery.jsx">
      <div className={cx("mb-5 border", {
      "border-productDetails-active": selected,
      "border-lightgray": !selected
    })}>
        {Boolean(thumbNail?.playbackId) ? <VideoThumbnailWrapper key={thumbNail.id}>
            <Media src={`https://image.mux.com/${thumbNail.playbackId}/thumbnail.webp?fit_mode=preserve`} alt="" width={85} height={85} className="m-auto block" />
          </VideoThumbnailWrapper> : <Media src={thumbNail.imageURI} alt="" width={85} height={85} className="m-auto" />}
      </div>
    </div>;
};
const MediaGallery = props => {
  const {
    media,
    discount,
    productId,
    items,
    session,
    product,
    locale
  } = props;
  const baseImages = media?.filter(item => item?.imageType === "BASE" || Boolean(item?.playbackId)) || [];
  const thumbnailImages = media?.filter(item => item?.imageType === "THUMBNAIL" || Boolean(item?.playbackId)) || [];
  const isEnglish = locale === "en";
  const discounted = hasDiscount(discount);
  const hasMultipleImages = baseImages?.length > 1;
  const OPTIONS = {
    main: {
      axis: "x",
      dragFree: false,
      containScroll: "trimSnaps",
      direction: isEnglish ? "ltr" : "rtl"
    },
    thumbs: {
      axis: "y",
      dragFree: true,
      containScroll: "keepSnaps",
      slidesToScroll: "auto"
    },
    dots: {
      containScroll: "keepSnaps",
      dragFree: true,
      axis: "x",
      direction: isEnglish ? "ltr" : "rtl"
    }
  };
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel(OPTIONS.main);
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel(OPTIONS.thumbs);
  const [emblaDotRef, emblaDotApi] = useEmblaCarousel(OPTIONS.dots);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = useCarouselButtons(emblaThumbsApi);
  const {
    selectedIndex: dotIndex,
    scrollSnaps,
    onDotButtonClick
  } = useDotButton(emblaMainApi, emblaDotApi);
  const onThumbClick = useCallback(index => {
    if (!emblaMainApi) return;
    emblaMainApi.scrollTo(index);
  }, [emblaMainApi]);
  const onSelect = useCallback(() => {
    if (!emblaMainApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    if (emblaDotApi) {
      emblaDotApi.scrollTo(emblaMainApi.selectedScrollSnap());
    }
  }, [emblaMainApi, emblaDotApi]);
  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();
    emblaMainApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaMainApi, onSelect]);
  const isThumbnailCarouselRequired = thumbnailImages?.length > 5;
  return <div className="md:flex md:space-x-5 md:rtl:space-x-reverse" data-sentry-component="MediaGallery" data-sentry-source-file="MediaGallery.jsx">
      <div className="relative hidden md:flex md:flex-col md:items-center md:justify-start">
        {isThumbnailCarouselRequired && <ThumbsCarouselButton direction="prev" onClick={onPrevButtonClick} disabled={prevBtnDisabled} className="hidden pb-3 md:block" />}
        <div className="mx-auto"
      //TODO: Changed direction from LTR to RTL for arabic
      dir="ltr">
          <div className="overflow-hidden" ref={emblaThumbsRef}>
            <div className="md:h-[520px]">
              {thumbnailImages?.map((item, index) => <Thumb key={item.id} onClick={() => onThumbClick(index)} selected={index === selectedIndex} thumbNail={item} />)}
            </div>
          </div>
        </div>
        {isThumbnailCarouselRequired && <ThumbsCarouselButton direction="next" onClick={onNextButtonClick} disabled={nextBtnDisabled} className="hidden pt-3 md:block" />}
      </div>
      <div className="relative">
        {discounted && <BadgeDiscount discount={discount} variant="md" isPdp />}
        <ToggleWishlistButton productId={productId} items={items} session={session} variant="md" isPdp product={product} data-sentry-element="ToggleWishlistButton" data-sentry-source-file="MediaGallery.jsx" />
        <div className="relative overflow-hidden md:mx-0 md:max-w-[520px]" ref={emblaMainRef}>
          <div className="flex touch-pan-y touch-pinch-zoom">
            {baseImages?.map((item, index) => <div className="min-w-0 flex-[0_0_100%]" key={item?.id}>
                {Boolean(item?.playbackId) ? <EmbedVideo data={{
              enable: true,
              autoPlay: false,
              mux_video_asset: {
                playback_id: item.playbackId
              },
              paused: index === selectedIndex,
              isFullScreenWidth: false
            }} /> : <Media src={item.imageURI} alt="" width={item.width > 0 ? item.width : 800} height={item.height > 0 ? item.height : 800} priority />}
              </div>)}
          </div>
        </div>
        {hasMultipleImages && <div className={cx("absolute bottom-4 left-1/2 z-10 -translate-x-1/2 overflow-hidden md:hidden", {
        "w-[138px]": isThumbnailCarouselRequired
      })} ref={emblaDotRef}>
            <div className="flex gap-2">
              {scrollSnaps?.map((_, index) => <DotButton key={index} onClick={() => onDotButtonClick(index)} className={cx("h-[4px] w-[20px] flex-shrink-0", {
            "bg-productDetails-active": index === selectedIndex,
            "bg-productDetails-bg border-white": index !== selectedIndex
          })} aria-label={index} />)}
            </div>
          </div>}
      </div>
    </div>;
};
export default MediaGallery;